'use client';

import { useState, useCallback, useEffect, useRef, useMemo } from 'react';
import dynamic from 'next/dynamic';
import clsx from 'clsx';
import {
  Search,
  SlidersHorizontal,
  X,
  RotateCcw,
  Loader2,
  Map,
  List,
  Expand,
  Undo2,
  MapPin,
  Grid3x3,
} from 'lucide-react';
import { useDebouncedValue } from '@/lib/hooks';
import { useEarlyPreviewBanner } from '@/components/EarlyPreviewProvider';
import type { MapBrowserObject, MapLegendConfig } from './ObjectMapBrowser';
import {
  Combobox,
  ComboboxChip,
  ComboboxChips,
  ComboboxChipsInput,
  ComboboxContent,
  ComboboxEmpty,
  ComboboxItem,
  ComboboxList,
  ComboboxValue,
  useComboboxAnchor,
} from '@/components/ui/combobox';
import { cn } from '@/lib/utils';
import { BrowserNavigationProvider } from '@/lib/ui/BrowserNavigationContext';
import { buildEntityNavigationList } from '@/lib/hooks/useEntityDetailSheet';
import { buildInitiativeNavigationList } from '@/lib/hooks/useInitiativeDetailSheet';
import type { Entity, ApiInitiative } from '@/lib/api/types';

const EMPTY_FILTERS: Record<string, string[]> = {};

// Dynamically import the map to avoid SSR issues with Leaflet
const ObjectMapView = dynamic(() => import('./ObjectMapView').then((mod) => mod.default), {
  ssr: false,
  loading: () => (
    <div className="w-full h-full flex items-center justify-center bg-gray-100 rounded-xl">
      <Loader2 className="w-8 h-8 text-gray-400 animate-spin" />
    </div>
  ),
}) as React.ComponentType<{
  objects: MapBrowserObject[];
  center: [number, number];
  zoom: number;
  initialBounds?: [[number, number], [number, number]];
  maxZoom?: number;
  minZoom?: number;
  scrollWheelZoom?: boolean;
  dragging?: boolean;
  zoomSnap?: number;
  zoomDelta?: number;
  wheelPxPerZoomLevel?: number;
  noWrap?: boolean;
  maxBounds?: [[number, number], [number, number]];
  initialZoomMode?: string;
  hoveredId: string | number | null;
  onMarkerHover: (id: string | number | null) => void;
  onShowDetail?: (obj: MapBrowserObject) => void;
  onViewChange?: (center: [number, number], zoom: number) => void;
  legend?: MapLegendConfig;
}>;

// --- Types ---

export interface BrowserFilterOption {
  id: string;
  label: string;
}

export interface BrowserFilterConfig {
  key: string;
  label: string;
  options: BrowserFilterOption[];
  /** Whether multiple values can be selected. Defaults to true */
  multi?: boolean;
  /** How to render the filter. 'checkbox' (default) shows a scrollable checkbox list. 'combobox' shows a searchable popover with chips. */
  displayMode?: 'checkbox' | 'combobox';
}

export interface BrowserFetcherResult<T> {
  objects: T[];
  hasNextPage: boolean;
  nextCursor?: string;
  totalCount?: number;
}

/** Map configuration for ObjectBrowser */
export interface BrowserMapConfig {
  /** Initial map center [lat, lng] */
  initialCenter?: [number, number];
  /** Initial map zoom level. Can be a number or a string enum (e.g. "contain") for dynamic calculation. */
  initialZoom?: number | string;
  /** Initial map bounds [[south, west], [north, east]]. Overrides initialCenter/initialZoom. */
  initialBounds?: [[number, number], [number, number]];
  /** Maximum zoom level */
  maxZoom?: number;
  /** Minimum zoom level */
  minZoom?: number;
  /** Whether scroll wheel zoom is enabled. Defaults to true. */
  scrollWheelZoom?: boolean;
  /** Whether map dragging/panning is enabled. Defaults to true. */
  dragging?: boolean;
  /** Snap value for zoom levels (e.g. 0.25 allows quarter-zoom increments). */
  zoomSnap?: number;
  /** Zoom level delta for keyboard/button zoom. */
  zoomDelta?: number;
  /** Pixels of wheel scroll per full zoom level change. Higher = slower zoom. */
  wheelPxPerZoomLevel?: number;
  /** Show a button to reset the map to its initial view. Defaults to true. */
  showResetView?: boolean;
  /** Whether map is visible by default on desktop. Defaults to true */
  defaultShowMap?: boolean;
  /** Callback when a map marker detail is clicked. Receives the clicked object and the full loaded objects array. */
  onShowDetail?: (obj: MapBrowserObject, allObjects?: MapBrowserObject[]) => void;
  /** Whether the map feature is enabled. Defaults to true. Set to false to completely disable the map. */
  enabled?: boolean;
  /** Whether to show the map when there are no results. Defaults to false. */
  showMapOnEmpty?: boolean;
  /** Legend / key to overlay on the map */
  legend?: MapLegendConfig;
  /** Disable infinite horizontal wrapping on the tile layer. Defaults to false. */
  noWrap?: boolean;
  /** Restrict panning to within these bounds [[south, west], [north, east]]. */
  maxBounds?: [[number, number], [number, number]];
}

export interface ObjectBrowserProps<T> {
  /** Async fetcher function. Receives search, filters, cursor, limit. */
  fetcher: (params: {
    search?: string;
    filters: Record<string, string[]>;
    cursor?: string;
    limit: number;
  }) => Promise<BrowserFetcherResult<T>>;
  /** Render a single result card (used in grid mode) */
  renderCard: (object: T) => React.ReactNode;
  /** Render a single result row (used in list mode). Falls back to renderCard if not provided. */
  renderListItem?: (object: T) => React.ReactNode;
  /** Unique key extractor */
  getKey: (object: T) => string | number;
  /** Filter definitions */
  filters?: BrowserFilterConfig[];
  /** Whether search is enabled. Defaults to true */
  searchEnabled?: boolean;
  /** Search placeholder */
  searchPlaceholder?: string;
  /** Page size */
  limit?: number;
  /** Empty state */
  emptyState?: { title: string; description: string; compact?: boolean };
  /** Optional className */
  className?: string;
  /** When this value changes, a refetch is triggered. Useful for external state (e.g. date range) that lives outside the browser's own search/filter state. */
  fetchKey?: string;
  /** Additional content rendered inside the filter sidebar (desktop) and mobile filter overlay, after the search input and before the filter groups. */
  filterSlot?: React.ReactNode;
  /** Initial search value */
  initialSearch?: string;
  /** Initial filter values */
  initialFilters?: Record<string, string[]>;
  /** Called when search changes (for URL sync) */
  onSearchChange?: (search: string) => void;
  /** Called when filters change (for URL sync) */
  onFiltersChange?: (filters: Record<string, string[]>) => void;
  /** Extra chips to render alongside the active filter chips (e.g. date range) */
  filterChipSlot?: React.ReactNode;
  /** Convert an object to a MapBrowserObject. When provided, enables map view. */
  toMapObject?: (object: T) => MapBrowserObject;
  /** Map configuration options. Only used when toMapObject is provided. */
  mapConfig?: BrowserMapConfig;
  /** Show the result count header (e.g. "Showing 12 results"). Defaults to true. */
  showResultCount?: boolean;
  /** Force the filter layout mode. 'desktop' = always sidebar, 'mobile' = always overlay, 'auto' = responsive (default). */
  filterLayout?: 'desktop' | 'mobile' | 'auto';
  /** Display view mode. 'grid' (default) or 'list'. */
  viewMode?: 'grid' | 'list';
  /** Called when the user toggles the view mode. */
  onViewModeChange?: (mode: 'grid' | 'list') => void;
  /** Show the grid/list view mode toggle buttons. Defaults to true. */
  showViewModeToggle?: boolean;
  /** Hide the results header (count + view toggle) when there are no results. Defaults to false. */
  hideHeaderOnEmpty?: boolean;
  /** Control the minimum height behavior. 'full' (default) uses viewport-based min-heights for full-page layouts. 'none' removes all min-height constraints for embedded/compact usage. A string value is applied as a custom className. */
  minHeight?: 'full' | 'none' | string;
  /** Scroll to top of the page when search or filters change. Defaults to true. Set to false for embedded usage. */
  scrollOnChange?: boolean;
  /** Number of grid columns on the smallest (mobile) breakpoint. Defaults to 2. */
  mobileColumns?: 1 | 2;
}

/** Combobox-style filter using the shadcn Combobox (Base UI) with chips for multi-select */
function ComboboxFilter({
  config,
  selected,
  onToggle,
  onClear,
}: {
  config: BrowserFilterConfig;
  selected: string[];
  onToggle: (value: string) => void;
  onClear: () => void;
}) {
  const anchorRef = useComboboxAnchor();

  // Use labels as the combobox items; map between labels and IDs
  const optionLabels = config.options.map((o) => o.label);
  const labelToId: Record<string, string> = {};
  const idToLabel: Record<string, string> = {};
  for (const o of config.options) {
    labelToId[o.label] = o.id;
    idToLabel[o.id] = o.label;
  }
  const selectedLabels = selected.map((id) => idToLabel[id] || id);

  const handleValueChange = (newLabels: string[]) => {
    const added = newLabels.find((l) => !selectedLabels.includes(l));
    const removed = selectedLabels.find((l) => !newLabels.includes(l));
    const id = added ? labelToId[added] : removed ? labelToId[removed] : undefined;
    if (id) onToggle(id);
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-1.5">
        <label className="text-sm font-medium text-gray-700">{config.label}</label>
        {selected.length > 0 && (
          <button
            onClick={onClear}
            className="text-xs text-gray-400 hover:text-gray-600 cursor-pointer"
          >
            Clear
          </button>
        )}
      </div>
      <Combobox
        items={optionLabels}
        multiple
        value={selectedLabels}
        onValueChange={handleValueChange}
      >
        <ComboboxChips ref={anchorRef}>
          <ComboboxValue>
            {selectedLabels.map((label) => (
              <ComboboxChip key={label}>{label}</ComboboxChip>
            ))}
          </ComboboxValue>
          <ComboboxChipsInput placeholder={selected.length === 0 ? 'All' : `Search...`} />
        </ComboboxChips>
        <ComboboxContent anchor={anchorRef} side="bottom" align="start">
          <ComboboxEmpty>No results found.</ComboboxEmpty>
          <ComboboxList>
            {(item: string) => (
              <ComboboxItem key={item} value={item}>
                {item}
              </ComboboxItem>
            )}
          </ComboboxList>
        </ComboboxContent>
      </Combobox>
    </div>
  );
}

export function ObjectBrowser<T>({
  fetcher,
  renderCard,
  renderListItem,
  getKey,
  filters: filterConfigs = [],
  searchEnabled = true,
  searchPlaceholder = 'Search...',
  limit = 40,
  emptyState,
  className,
  fetchKey,
  filterSlot,
  initialSearch = '',
  initialFilters = EMPTY_FILTERS,
  onSearchChange,
  onFiltersChange,
  filterChipSlot,
  toMapObject,
  mapConfig,
  showResultCount = false,
  filterLayout = 'auto',
  viewMode: viewModeProp = 'grid',
  onViewModeChange,
  showViewModeToggle = true,
  hideHeaderOnEmpty = false,
  minHeight = 'full',
  scrollOnChange = true,
  mobileColumns = 2,
}: ObjectBrowserProps<T>) {
  const [searchQuery, setSearchQuery] = useState(initialSearch);
  const [activeFilters, setActiveFilters] = useState<Record<string, string[]>>(initialFilters);
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);
  // Temp filters for the mobile overlay (apply on search click)
  const [tempFilters, setTempFilters] = useState<Record<string, string[]>>(initialFilters);
  const [tempSearch, setTempSearch] = useState(initialSearch);

  const [objects, setObjects] = useState<T[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isRefetching, setIsRefetching] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const hasFetchedOnce = useRef(false);
  const [hasNextPage, setHasNextPage] = useState(false);
  const [nextCursor, setNextCursor] = useState<string | undefined>();
  const [totalCount, setTotalCount] = useState<number | undefined>();

  // Early preview banner offset
  const isBannerVisible = useEarlyPreviewBanner();
  const bannerOffset = isBannerVisible ? 36 : 0;

  // Map state
  const mapEnabled = !!toMapObject && mapConfig?.enabled !== false;
  const [showMap, setShowMap] = useState(mapConfig?.defaultShowMap ?? true);
  const [mapExpanded, setMapExpanded] = useState(false);
  const [mobileView, setMobileView] = useState<'list' | 'map'>('list');
  const [hoveredId, setHoveredId] = useState<string | number | null>(null);
  const [mapCenter, setMapCenter] = useState<[number, number]>(
    mapConfig?.initialCenter ?? [-41.2865, 174.7762]
  );
  const [mapZoom, setMapZoom] = useState(
    typeof mapConfig?.initialZoom === 'number' ? mapConfig.initialZoom : 6
  );
  const [mapResetKey, setMapResetKey] = useState(0);

  // Resolve initialZoomMode from string-based initialZoom
  const initialZoomMode =
    typeof mapConfig?.initialZoom === 'string' ? mapConfig.initialZoom : undefined;

  const debouncedSearch = useDebouncedValue(searchQuery, 300);

  // View mode state (controlled or uncontrolled)
  const [internalViewMode, setInternalViewMode] = useState(viewModeProp);
  const currentViewMode = viewModeProp ?? internalViewMode;

  useEffect(() => {
    setInternalViewMode(viewModeProp);
  }, [viewModeProp]);

  const handleViewModeToggle = (mode: 'grid' | 'list') => {
    setInternalViewMode(mode);
    onViewModeChange?.(mode);
  };

  // Track internal changes to avoid redundant syncs from URL round-trips.
  // Uses a counter so concurrent search + filter changes don't interfere.
  const internalChangeCountRef = useRef(0);

  // Sync props changes (only from external sources like browser back/forward)
  useEffect(() => {
    if (internalChangeCountRef.current > 0) {
      internalChangeCountRef.current--;
      return;
    }
    setSearchQuery(initialSearch);
  }, [initialSearch]);

  // Serialize initialFilters for stable dependency comparison
  const initialFiltersKey = JSON.stringify(initialFilters);

  useEffect(() => {
    if (internalChangeCountRef.current > 0) {
      internalChangeCountRef.current--;
      return;
    }
    setActiveFilters(initialFilters);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialFiltersKey]);

  // Fetch on search/filter change
  const fetcherRef = useRef(fetcher);
  fetcherRef.current = fetcher;

  useEffect(() => {
    let cancelled = false;

    // First load → full loading state; subsequent changes → subtle refetch indicator
    if (!hasFetchedOnce.current) {
      setIsLoading(true);
    } else {
      setIsRefetching(true);
    }

    fetcherRef
      .current({ search: debouncedSearch || undefined, filters: activeFilters, limit })
      .then((result) => {
        if (cancelled) return;
        setObjects(result.objects);
        setHasNextPage(result.hasNextPage);
        setNextCursor(result.nextCursor);
        if (result.totalCount !== undefined) setTotalCount(result.totalCount);
        hasFetchedOnce.current = true;
      })
      .catch(() => {
        if (!cancelled) setObjects([]);
      })
      .finally(() => {
        if (!cancelled) {
          setIsLoading(false);
          setIsRefetching(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [debouncedSearch, activeFilters, limit, fetchKey]);

  const handleLoadMore = useCallback(async () => {
    if (isLoadingMore || !hasNextPage) return;
    setIsLoadingMore(true);
    try {
      const result = await fetcherRef.current({
        search: debouncedSearch || undefined,
        filters: activeFilters,
        cursor: nextCursor,
        limit,
      });
      setObjects((prev) => [...prev, ...result.objects]);
      setHasNextPage(result.hasNextPage);
      setNextCursor(result.nextCursor);
    } finally {
      setIsLoadingMore(false);
    }
  }, [debouncedSearch, activeFilters, nextCursor, hasNextPage, isLoadingMore, limit]);

  const handleSearchInput = (value: string) => {
    internalChangeCountRef.current++;
    setSearchQuery(value);
    onSearchChange?.(value);
    if (scrollOnChange) window.scrollTo({ top: 0 });
  };

  const updateFilters = (newFilters: Record<string, string[]>) => {
    internalChangeCountRef.current++;
    setActiveFilters(newFilters);
    onFiltersChange?.(newFilters);
    if (scrollOnChange) window.scrollTo({ top: 0 });
  };

  const toggleFilterValue = (key: string, value: string) => {
    const current = activeFilters[key] || [];
    const next = current.includes(value) ? current.filter((v) => v !== value) : [...current, value];
    updateFilters({ ...activeFilters, [key]: next });
  };

  const clearFilter = (key: string) => {
    const next = { ...activeFilters };
    delete next[key];
    updateFilters(next);
  };

  const clearAllFilters = () => {
    // updateFilters already adds +1 for the filter sync. Only add +1 for the
    // search sync when the search actually changes — otherwise the initialSearch
    // effect never re-runs to consume the increment, leaving the counter stuck
    // and causing the next external filter sync to be wrongly skipped.
    if (searchQuery !== '') {
      internalChangeCountRef.current++;
    }
    updateFilters({});
    setSearchQuery('');
    onSearchChange?.('');
  };

  const hasActiveFilters = Object.values(activeFilters).some((v) => v.length > 0);

  // Mobile overlay handlers
  const openMobileFilters = () => {
    setTempFilters({ ...activeFilters });
    setTempSearch(searchQuery);
    setMobileFiltersOpen(true);
  };

  const applyMobileFilters = () => {
    // Both search and filters change — skip both sync effects
    internalChangeCountRef.current += 2;
    setActiveFilters(tempFilters);
    setSearchQuery(tempSearch);
    onFiltersChange?.(tempFilters);
    onSearchChange?.(tempSearch);
    setMobileFiltersOpen(false);
  };

  const resetMobileFilters = () => {
    setTempFilters({});
    setTempSearch('');
  };

  // --- Map helpers ---
  const mapObjects = useMemo(() => {
    if (!toMapObject) return [];
    return objects.map(toMapObject);
  }, [objects, toMapObject]);

  const mappableObjects = useMemo(
    () => mapObjects.filter((o) => o.lat != null && o.lng != null),
    [mapObjects]
  );

  const handleMapViewChange = useCallback((center: [number, number], zoom: number) => {
    setMapCenter(center);
    setMapZoom(zoom);
  }, []);

  const handleResetMapView = useCallback(() => {
    if (mapConfig?.initialBounds) {
      // When using bounds, reset center/zoom to defaults so the map re-applies bounds
      setMapCenter(mapConfig?.initialCenter ?? [-41.2865, 174.7762]);
      setMapZoom(typeof mapConfig?.initialZoom === 'number' ? mapConfig.initialZoom : 6);
      // Force a re-render of the map by toggling a key — bounds are only applied on mount
      setMapResetKey((k) => k + 1);
    } else if (initialZoomMode) {
      // For dynamic zoom modes, just force a re-mount to re-calculate
      setMapCenter(mapConfig?.initialCenter ?? [-41.2865, 174.7762]);
      setMapResetKey((k) => k + 1);
    } else {
      setMapCenter(mapConfig?.initialCenter ?? [-41.2865, 174.7762]);
      setMapZoom(typeof mapConfig?.initialZoom === 'number' ? mapConfig.initialZoom : 6);
    }
  }, [mapConfig?.initialBounds, mapConfig?.initialCenter, mapConfig?.initialZoom, initialZoomMode]);

  // --- Filter sidebar (desktop) ---
  const showSidebar = filterConfigs.length > 0 || searchEnabled;
  const sidebarVisibility =
    filterLayout === 'desktop' ? 'block' : filterLayout === 'mobile' ? 'hidden' : 'hidden lg:block';
  const filterSidebar = showSidebar && (
    <div className={clsx(sidebarVisibility, 'w-[280px] shrink-0')}>
      <div
        className="bg-white rounded-xl border border-gray-200 p-2 sticky space-y-6"
        style={{ top: 80 + bannerOffset }}
      >
        {searchEnabled && (
          <div>
            <div className="relative">
              <Search className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
              <input
                type="text"
                placeholder={searchPlaceholder}
                value={searchQuery}
                onChange={(e) => handleSearchInput(e.target.value)}
                className="w-full pl-9 pr-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-transparent outline-none"
              />
            </div>
          </div>
        )}

        {filterSlot}

        {filterConfigs.map((config) => {
          const selected = activeFilters[config.key] || [];

          if (config.displayMode === 'combobox') {
            return (
              <ComboboxFilter
                key={config.key}
                config={config}
                selected={selected}
                onToggle={(value) => toggleFilterValue(config.key, value)}
                onClear={() => clearFilter(config.key)}
              />
            );
          }

          return (
            <div key={config.key}>
              <div className="flex items-center justify-between mb-1.5">
                <label className="text-sm font-medium text-gray-700">{config.label}</label>
                {selected.length > 0 && (
                  <button
                    onClick={() => clearFilter(config.key)}
                    className="text-xs text-gray-400 hover:text-gray-600 cursor-pointer"
                  >
                    Clear
                  </button>
                )}
              </div>
              <div className="space-y-1 max-h-96 overflow-y-auto">
                {config.options.map((opt) => (
                  <label
                    key={opt.id}
                    className="flex items-center gap-2 px-2 py-1 rounded-md hover:bg-gray-50 cursor-pointer text-sm"
                  >
                    <input
                      type="checkbox"
                      checked={selected.includes(opt.id)}
                      onChange={() => toggleFilterValue(config.key, opt.id)}
                      className="rounded border-gray-300 text-primary-500 focus:ring-primary-500"
                    />
                    <span className="text-gray-700">{opt.label}</span>
                  </label>
                ))}
              </div>
            </div>
          );
        })}

        {(hasActiveFilters || searchQuery.length > 0) && (
          <button
            onClick={clearAllFilters}
            className="flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-700 cursor-pointer"
          >
            <RotateCcw className="w-3.5 h-3.5" />
            Reset filters
          </button>
        )}
      </div>
    </div>
  );

  // --- Mobile filter overlay ---
  const mobileFilterOverlay = mobileFiltersOpen && (
    <div
      className={clsx(
        'fixed z-50 bg-white flex flex-col',
        filterLayout === 'mobile'
          ? 'inset-x-0 top-1/4 bottom-0 rounded-t-2xl shadow-2xl border-t border-gray-200 lg:inset-x-auto lg:top-1/2 lg:left-1/2 lg:-translate-x-1/2 lg:-translate-y-1/2 lg:bottom-auto lg:w-[480px] lg:max-h-[70vh] lg:rounded-2xl lg:border lg:shadow-xl'
          : filterLayout === 'desktop'
            ? 'hidden'
            : 'inset-0 lg:hidden'
      )}
    >
      <div className="flex items-center gap-2 p-4 border-b border-gray-200">
        <button
          onClick={() => setMobileFiltersOpen(false)}
          className="w-9 h-9 flex items-center justify-center rounded-lg border border-gray-200 cursor-pointer"
        >
          <X className="w-4 h-4" />
        </button>
        <button
          onClick={resetMobileFilters}
          className="w-9 h-9 flex items-center justify-center rounded-lg border border-gray-200 cursor-pointer"
        >
          <RotateCcw className="w-4 h-4" />
        </button>
        <button
          onClick={applyMobileFilters}
          className="flex-1 flex items-center justify-center gap-2 py-2.5 bg-primary-500 text-white font-medium rounded-lg hover:bg-primary-600 cursor-pointer"
        >
          <Search className="w-4 h-4" />
          Search
        </button>
      </div>

      <div className="flex-1 overflow-y-auto p-4 space-y-6">
        {searchEnabled && (
          <div>
            <label className="text-sm font-medium text-gray-700 mb-1.5 block">Search</label>
            <input
              type="text"
              placeholder={searchPlaceholder}
              value={tempSearch}
              onChange={(e) => setTempSearch(e.target.value)}
              className="w-full px-3 py-2 border-b border-gray-200 text-sm focus:border-primary-500 outline-none"
            />
          </div>
        )}

        {filterSlot}

        {filterConfigs.map((config) => {
          const selected = tempFilters[config.key] || [];

          if (config.displayMode === 'combobox') {
            return (
              <ComboboxFilter
                key={config.key}
                config={config}
                selected={selected}
                onToggle={(value) => {
                  setTempFilters((prev) => {
                    const curr = prev[config.key] || [];
                    const next = curr.includes(value)
                      ? curr.filter((v) => v !== value)
                      : [...curr, value];
                    return { ...prev, [config.key]: next };
                  });
                }}
                onClear={() => setTempFilters((prev) => ({ ...prev, [config.key]: [] }))}
              />
            );
          }

          return (
            <div key={config.key}>
              <div className="flex items-center justify-between mb-1.5">
                <label className="text-sm font-medium text-gray-700">{config.label}</label>
                {selected.length > 0 && (
                  <button
                    onClick={() => setTempFilters((prev) => ({ ...prev, [config.key]: [] }))}
                    className="text-xs text-gray-400 hover:text-gray-600 cursor-pointer"
                  >
                    Clear
                  </button>
                )}
              </div>
              <div className="space-y-1">
                {config.options.map((opt) => {
                  const isSelected = selected.includes(opt.id);
                  return (
                    <label
                      key={opt.id}
                      className="flex items-center gap-2 px-2 py-2 rounded-md hover:bg-gray-50 cursor-pointer text-sm"
                    >
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={() => {
                          setTempFilters((prev) => {
                            const curr = prev[config.key] || [];
                            const next = isSelected
                              ? curr.filter((v) => v !== opt.id)
                              : [...curr, opt.id];
                            return { ...prev, [config.key]: next };
                          });
                        }}
                        className="rounded border-gray-300 text-primary-500 focus:ring-primary-500"
                      />
                      <span className="text-gray-700">{opt.label}</span>
                    </label>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );

  // --- Active filter chips ---
  const hasChips = hasActiveFilters || !!filterChipSlot || !!debouncedSearch;
  const activeFilterChips = (
    <div className="flex flex-wrap gap-1.5">
      {filterChipSlot}
      {debouncedSearch && (
        <span className="inline-flex items-center gap-1 px-2.5 py-1 bg-primary-50 text-primary-700 rounded-full text-xs font-medium max-w-[12rem]">
          <Search className="w-3 h-3 shrink-0" />
          <span className="truncate">{debouncedSearch}</span>
          <button
            onClick={() => handleSearchInput('')}
            className="hover:text-primary-900 cursor-pointer shrink-0"
          >
            <X className="w-3 h-3" />
          </button>
        </span>
      )}
      {Object.entries(activeFilters).map(([key, values]) => {
        const config = filterConfigs.find((f) => f.key === key);
        if (!config || values.length === 0) return null;
        return values.map((val) => {
          const opt = config.options.find((o) => o.id === val);
          return (
            <span
              key={`${key}-${val}`}
              className="inline-flex items-center gap-1 px-2.5 py-1 bg-primary-50 text-primary-700 rounded-full text-xs font-medium"
            >
              {opt?.label || val}
              <button
                onClick={() => toggleFilterValue(key, val)}
                className="hover:text-primary-900 cursor-pointer"
              >
                <X className="w-3 h-3" />
              </button>
            </span>
          );
        });
      })}
      {(hasActiveFilters || debouncedSearch) && (
        <button
          onClick={clearAllFilters}
          className="text-xs text-gray-400 hover:text-gray-600 px-2 py-1 cursor-pointer"
        >
          Clear all
        </button>
      )}
    </div>
  );

  // --- Results count ---
  const resultsCount = objects.length;

  // --- Shared content pieces ---
  const loadMoreButton = hasNextPage && (
    <button
      onClick={handleLoadMore}
      disabled={isLoadingMore}
      className="w-full mt-4 px-4 py-2.5 bg-primary-500 text-white font-medium rounded-xl hover:bg-primary-600 transition-colors text-sm disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer"
    >
      {isLoadingMore ? 'Loading...' : 'Load more'}
    </button>
  );

  const showMapOnEmpty = mapEnabled && (mapConfig?.showMapOnEmpty ?? false);

  const resultsHeader = showResultCount && (
    <div className="flex items-center justify-between mb-4">
      <p className="text-sm text-gray-500">
        {isLoading
          ? 'Loading...'
          : `Showing ${resultsCount} result${resultsCount !== 1 ? 's' : ''}`}
        {isRefetching && !isLoading && (
          <Loader2 className="inline-block w-3.5 h-3.5 ml-2 animate-spin text-gray-400" />
        )}
        {mapEnabled && showMap && mappableObjects.length > 0 && !isLoading && (
          <span className="ml-1">· {mappableObjects.length} on map</span>
        )}
      </p>
      <div className="flex items-center gap-2">
        {/* View mode toggle */}
        {!mapEnabled && showViewModeToggle && (
          <div className="inline-flex rounded-lg border border-gray-200 bg-white p-0.5">
            <button
              onClick={() => handleViewModeToggle('grid')}
              className={clsx(
                'inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-sm font-medium transition-colors cursor-pointer',
                currentViewMode === 'grid'
                  ? 'bg-primary-50 text-primary-700'
                  : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
              )}
              title="Grid view"
            >
              <Grid3x3 className="w-4 h-4" />
            </button>
            <button
              onClick={() => handleViewModeToggle('list')}
              className={clsx(
                'inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-sm font-medium transition-colors cursor-pointer',
                currentViewMode === 'list'
                  ? 'bg-primary-50 text-primary-700'
                  : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
              )}
              title="List view"
            >
              <List className="w-4 h-4" />
            </button>
          </div>
        )}
        {mapEnabled && !isLoading && (objects.length > 0 || showMapOnEmpty) && (
          <button
            onClick={() => {
              setShowMap((v) => !v);
              setMapExpanded(false);
            }}
            className="hidden lg:flex items-center gap-1.5 px-3 py-1.5 text-sm text-gray-600 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors cursor-pointer"
            aria-label={showMap ? 'Hide map' : 'Show map'}
          >
            {showMap ? <List className="w-4 h-4" /> : <Map className="w-4 h-4" />}
            {showMap ? 'Hide map' : 'Show map'}
          </button>
        )}
      </div>
    </div>
  );

  // --- Map-enabled content ---
  const handleMapShowDetail = useCallback(
    (obj: MapBrowserObject) => {
      mapConfig?.onShowDetail?.(obj, mapObjects);
    },
    [mapConfig?.onShowDetail, mapObjects]
  );

  const mapElement = mapEnabled && (
    <ObjectMapView
      key={mapResetKey}
      objects={mappableObjects}
      center={mapCenter}
      zoom={mapZoom}
      initialBounds={mapConfig?.initialBounds}
      maxZoom={mapConfig?.maxZoom}
      minZoom={mapConfig?.minZoom}
      scrollWheelZoom={mapConfig?.scrollWheelZoom}
      dragging={mapConfig?.dragging}
      zoomSnap={mapConfig?.zoomSnap}
      zoomDelta={mapConfig?.zoomDelta}
      wheelPxPerZoomLevel={mapConfig?.wheelPxPerZoomLevel}
      noWrap={mapConfig?.noWrap}
      maxBounds={mapConfig?.maxBounds}
      initialZoomMode={initialZoomMode}
      hoveredId={hoveredId}
      onMarkerHover={setHoveredId}
      onShowDetail={handleMapShowDetail}
      onViewChange={handleMapViewChange}
      legend={mapConfig?.legend}
    />
  );

  const emptyMessage = emptyState?.compact ? (
    <div className="text-center py-4">
      <p className="text-sm text-gray-500">
        {emptyState?.title || 'No results found'}
        {emptyState?.description ? ` · ${emptyState.description}` : ''}
      </p>
    </div>
  ) : (
    <div className="text-center py-16">
      {mapEnabled ? (
        <MapPin className="w-12 h-12 mx-auto mb-4 text-gray-300" />
      ) : (
        <Search className="w-12 h-12 mx-auto mb-4 text-gray-300" />
      )}
      <h3 className="text-lg font-semibold text-gray-900 mb-1">
        {emptyState?.title || 'No results found'}
      </h3>
      <p className="text-gray-500 text-sm">
        {emptyState?.description || 'Try adjusting your search or filters'}
      </p>
    </div>
  );

  const emptyContent = emptyMessage;

  const loadingContent = (
    <div className="flex items-center justify-center py-16">
      <Loader2 className="w-10 h-10 text-gray-400 animate-spin" />
    </div>
  );

  // --- Grid/List content (no map) ---
  const renderItem = currentViewMode === 'list' && renderListItem ? renderListItem : renderCard;

  const gridContent = (
    <>
      {currentViewMode === 'list' ? (
        <div className="space-y-2">
          {objects.map((obj) => (
            <div key={getKey(obj)}>{renderItem(obj)}</div>
          ))}
        </div>
      ) : (
        <div
          className={clsx(
            'grid gap-3',
            mobileColumns === 1
              ? 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-4'
              : 'grid-cols-2 sm:grid-cols-3 2xl:grid-cols-4'
          )}
        >
          {objects.map((obj) => (
            <div key={getKey(obj)}>{renderCard(obj)}</div>
          ))}
        </div>
      )}
      {loadMoreButton}
    </>
  );

  const mapListCards = (showHover: boolean) =>
    objects.map((obj) => {
      const key = getKey(obj);
      return (
        <div
          key={key}
          onMouseEnter={showHover ? () => setHoveredId(key) : undefined}
          onMouseLeave={showHover ? () => setHoveredId(null) : undefined}
          className={clsx(
            'transition-all duration-200 rounded-xl',
            showHover && hoveredId === key && 'shadow-md ring-1 ring-gray-300 scale-[1.02]'
          )}
        >
          {renderCard(obj)}
        </div>
      );
    });

  // Desktop map layout: side-by-side list + map
  const hasResults = objects.length > 0;
  const showMapLayout = showMapOnEmpty || hasResults;
  const desktopMapContent = mapEnabled && !isLoading && showMapLayout && (
    <>
      {/* Desktop: side-by-side layout */}
      <div className={clsx('hidden lg:flex gap-4', showMap ? 'flex-row' : '')}>
        {!mapExpanded && (
          <div
            className={clsx(
              'overflow-y-auto',
              showMap ? 'w-1/2 xl:w-[45%] max-h-[calc(100vh-12rem)]' : 'w-full'
            )}
          >
            {hasResults ? (
              <>
                <div
                  className={clsx(
                    'grid gap-3',
                    showMap
                      ? mobileColumns === 1
                        ? 'grid-cols-1 sm:grid-cols-2'
                        : 'grid-cols-2'
                      : mobileColumns === 1
                        ? 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-4'
                        : 'grid-cols-2 sm:grid-cols-3 2xl:grid-cols-4'
                  )}
                >
                  {mapListCards(true)}
                </div>
                {loadMoreButton}
              </>
            ) : (
              emptyMessage
            )}
          </div>
        )}

        <div
          className={clsx(
            'h-[calc(100vh-12rem)] rounded-xl overflow-hidden border border-gray-200 sticky relative isolate',
            showMap ? (mapExpanded ? 'w-full' : 'w-1/2 xl:w-[55%]') : 'hidden'
          )}
          style={{ top: 16 + bannerOffset }}
        >
          <div className="absolute top-3 right-3 z-[500] flex gap-2">
            {mapConfig?.showResetView !== false && (
              <button
                onClick={handleResetMapView}
                className="w-10 h-10 bg-white rounded-lg shadow-md border border-gray-200 flex items-center justify-center hover:bg-gray-50 transition-colors cursor-pointer"
                aria-label="Reset map view"
              >
                <Undo2 className="w-5 h-5 text-gray-700" />
              </button>
            )}
            <button
              onClick={() => setMapExpanded((v) => !v)}
              className="w-10 h-10 bg-white rounded-lg shadow-md border border-gray-200 flex items-center justify-center hover:bg-gray-50 transition-colors cursor-pointer"
              aria-label={mapExpanded ? 'Shrink map' : 'Expand map'}
            >
              {mapExpanded ? (
                <X className="w-5 h-5 text-gray-700" />
              ) : (
                <Expand className="w-5 h-5 text-gray-700" />
              )}
            </button>
          </div>
          {mapElement}
        </div>
      </div>

      {/* Mobile: toggled list or map */}
      <div
        className={clsx('lg:hidden relative', minHeight === 'full' && 'min-h-[calc(100vh-12rem)]')}
      >
        <div className={clsx(mobileView !== 'list' && 'hidden')}>
          {hasResults ? (
            <>
              <div
                className={clsx(
                  'grid gap-3',
                  mobileColumns === 1
                    ? 'grid-cols-1 sm:grid-cols-2 md:grid-cols-4'
                    : 'grid-cols-2 xs:grid-cols-2 md:grid-cols-4'
                )}
              >
                {mapListCards(false)}
              </div>
              {loadMoreButton}
            </>
          ) : (
            emptyMessage
          )}
        </div>

        <div
          className={clsx(
            'rounded-xl overflow-hidden border border-gray-200 relative isolate',
            mobileView === 'map' ? 'h-[calc(100vh-12rem)]' : 'hidden'
          )}
        >
          {mapConfig?.showResetView !== false && (
            <button
              onClick={handleResetMapView}
              className="absolute top-3 right-3 z-[500] w-10 h-10 bg-white rounded-lg shadow-md border border-gray-200 flex items-center justify-center hover:bg-gray-50 transition-colors cursor-pointer"
              aria-label="Reset map view"
            >
              <Undo2 className="w-5 h-5 text-gray-700" />
            </button>
          )}
          {mapElement}
        </div>
      </div>

      {/* Mobile toggle button */}
      <div className="lg:hidden sticky bottom-0 z-20 pb-2 pt-2 flex justify-center pointer-events-none">
        <button
          onClick={() => {
            setMobileView((v) => (v === 'list' ? 'map' : 'list'));
            window.scrollTo({ top: 0, behavior: 'instant' as ScrollBehavior });
          }}
          className="flex items-center gap-2 px-5 py-3 bg-gray-900 text-white text-sm font-medium rounded-full shadow-lg hover:bg-gray-800 transition-colors cursor-pointer pointer-events-auto"
        >
          {mobileView === 'list' ? (
            <>
              <Map className="w-4 h-4" />
              Show map
            </>
          ) : (
            <>
              <List className="w-4 h-4" />
              Show list
            </>
          )}
        </button>
      </div>
    </>
  );

  // --- Build navigation lists from loaded objects for card context ---
  const entityNavList = useMemo(() => {
    if (!toMapObject) return undefined;
    const entityObjs = mapObjects.filter((o) => o.objectType === 'entity');
    if (entityObjs.length <= 1) return undefined;
    return buildEntityNavigationList(
      entityObjs.map((o) => o.data as Entity),
      'brand'
    );
  }, [mapObjects, toMapObject]);

  const initiativeNavList = useMemo(() => {
    if (!toMapObject) return undefined;
    const initObjs = mapObjects.filter((o) => o.objectType === 'initiative');
    if (initObjs.length <= 1) return undefined;
    return buildInitiativeNavigationList(initObjs.map((o) => o.data as ApiInitiative));
  }, [mapObjects, toMapObject]);

  return (
    <BrowserNavigationProvider
      entityNavigationList={entityNavList}
      initiativeNavigationList={initiativeNavList}
    >
      <div
        className={clsx(
          'w-full',
          minHeight === 'full' && 'min-h-[calc(100vh-5rem)]',
          minHeight !== 'full' && minHeight !== 'none' && minHeight,
          className
        )}
      >
        {mobileFiltersOpen && filterLayout === 'mobile' && (
          <div
            className="fixed inset-0 z-40 bg-black/30 hidden lg:block"
            onClick={() => setMobileFiltersOpen(false)}
          />
        )}
        {mobileFilterOverlay}

        {/* Mobile: search bar + filter button */}
        {(searchEnabled || filterConfigs.length > 0) && (
          <div
            className={clsx(
              'sticky z-20 bg-gradient-to-b from-gray-50 via-gray-50 to-gray-50/95 -mx-3 px-3 sm:-mx-6 sm:px-6 pt-2 pb-2',
              filterLayout === 'mobile'
                ? 'block'
                : filterLayout === 'desktop'
                  ? 'hidden'
                  : 'lg:hidden'
            )}
            style={{ top: 64 + bannerOffset }}
          >
            <div className="flex items-center gap-2">
              {searchEnabled && (
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
                  <input
                    type="text"
                    placeholder={searchPlaceholder}
                    value={searchQuery}
                    onChange={(e) => handleSearchInput(e.target.value)}
                    className="w-full pl-9 pr-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-transparent outline-none"
                  />
                </div>
              )}
              {filterConfigs.length > 0 && (
                <button
                  onClick={openMobileFilters}
                  className="flex items-center gap-1.5 px-3 py-2 border border-gray-200 rounded-lg text-sm text-gray-600 hover:bg-gray-50 shrink-0 cursor-pointer"
                >
                  Filters
                  <SlidersHorizontal className="w-4 h-4" />
                </button>
              )}
            </div>
            {hasChips && <div className="mt-2">{activeFilterChips}</div>}
          </div>
        )}

        {/* Desktop: sidebar + content */}
        <div className="flex gap-6">
          {filterSidebar}

          <div className="flex-1 min-w-0">
            {/* Desktop active filter chips (shown above results when sidebar has filters) */}
            {showSidebar && hasChips && (
              <div
                className={clsx(
                  'mb-4',
                  filterLayout === 'desktop'
                    ? 'block'
                    : filterLayout === 'mobile'
                      ? 'hidden'
                      : 'hidden lg:block'
                )}
              >
                {activeFilterChips}
              </div>
            )}

            {!(hideHeaderOnEmpty && !isLoading && objects.length === 0) && resultsHeader}

            <div
              className={clsx(
                'transition-opacity duration-200',
                isRefetching && !isLoading && 'opacity-60 pointer-events-none'
              )}
            >
              {isLoading
                ? loadingContent
                : objects.length === 0 && !showMapOnEmpty
                  ? emptyContent
                  : mapEnabled
                    ? desktopMapContent
                    : objects.length === 0
                      ? emptyContent
                      : gridContent}
            </div>
          </div>
        </div>
      </div>
    </BrowserNavigationProvider>
  );
}
